import React, { useEffect, useState, useMemo } from "react";
import { db } from "./firebase";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import "./Attach.css";

const DEBUG_DOC_PATH = {
  col: "adminSettings",
  id: "debugOverrides",
};

const EMPTY_OVERRIDES = {
  dpSold: 0,
  dpOpp: 0,
  mcEligible: 0,
  mcTotal: 0,
  mcApproved: 0,
  aalEligible: 0,
  aalHits: 0,
};

export default function Attach({ selectedProfile, lockedRepCode, isStoreMode }) {
  const [reports, setReports] = useState([]);
  const [products, setProducts] = useState([]);

  // store + rep overrides kept separate
  const [storeOverrides, setStoreOverrides] = useState(EMPTY_OVERRIDES);
  const [repOverrides, setRepOverrides] = useState(EMPTY_OVERRIDES);

  const [filterType, setFilterType] = useState("QTD");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    const fetchBaseData = async () => {
      try {
        const [reportSnapshot, productSnapshot, debugSnap] = await Promise.all([
          getDocs(collection(db, "eodReports")),
          getDocs(collection(db, "products")),
          getDoc(doc(db, DEBUG_DOC_PATH.col, DEBUG_DOC_PATH.id)),
        ]);

        const reportData = reportSnapshot.docs.map((docSnap) => docSnap.data());
        const productData = productSnapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));

        let debugData = { ...EMPTY_OVERRIDES };

        if (debugSnap.exists()) {
          const data = debugSnap.data();
          debugData = {
            dpSold: Number(data.dpSold || 0),
            dpOpp: Number(data.dpOpp || 0),
            mcEligible: Number(data.mcEligible || 0),
            mcTotal: Number(data.mcTotal || 0),
            mcApproved: Number(data.mcApproved || 0),
            aalEligible: Number(data.aalEligible || 0),
            aalHits: Number(data.aalHits || 0),
          };
        }

        setReports(reportData);
        setProducts(productData);
        setStoreOverrides(debugData); // store-level overrides
      } catch (error) {
        console.error("Failed to load attach data:", error);
      }
    };

    fetchBaseData();
  }, []);

  // fetch rep-specific overrides whenever lockedRepCode changes
  useEffect(() => {
    const fetchRepOverrides = async () => {
      if (!lockedRepCode || isStoreMode) {
        setRepOverrides(EMPTY_OVERRIDES);
        return;
      }

      try {
        const ref = doc(db, "repDebugOverrides", lockedRepCode);
        const snap = await getDoc(ref);

        if (!snap.exists()) {
          setRepOverrides(EMPTY_OVERRIDES);
          return;
        }

        const data = snap.data();
        setRepOverrides({
          dpSold: Number(data.dpSold || 0),
          dpOpp: Number(data.dpOpp || 0),
          mcEligible: Number(data.mcEligible || 0),
          mcTotal: Number(data.mcTotal || 0),
          mcApproved: Number(data.mcApproved || 0),
          aalEligible: Number(data.aalEligible || 0),
          aalHits: Number(data.aalHits || 0),
        });
      } catch (err) {
        console.error("Failed to load rep overrides:", err);
        setRepOverrides(EMPTY_OVERRIDES);
      }
    };

    fetchRepOverrides();
  }, [lockedRepCode, isStoreMode]);

  const productMap = useMemo(() => {
    const map = {};
    products.forEach((product) => {
      if (product?.name) {
        map[product.name] = product;
      }
    });
    return map;
  }, [products]);

  const filteredReports = useMemo(() => {
    const today = new Date();
    const todayMidnight = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );

    return reports.filter((r) => {
      if (!r.date) return false;

      const [y, m, d] = r.date.split("-").map(Number);
      const reportDate = new Date(y, m - 1, d);

      if (filterType === "ALL") return true;

      if (filterType === "MTD") {
        const monthStart = new Date(
          todayMidnight.getFullYear(),
          todayMidnight.getMonth(),
          1
        );
        return reportDate >= monthStart && reportDate <= todayMidnight;
      }

      if (filterType === "YTD") {
        const yearStart = new Date(todayMidnight.getFullYear(), 0, 1);
        return reportDate >= yearStart && reportDate <= todayMidnight;
      }

      if (filterType === "QTD") {
        const month = todayMidnight.getMonth();
        const quarterStartMonth = Math.floor(month / 3) * 3;
        const quarterStart = new Date(
          todayMidnight.getFullYear(),
          quarterStartMonth,
          1
        );
        return reportDate >= quarterStart && reportDate <= todayMidnight;
      }

      if (filterType === "RANGE") {
        if (!startDate || !endDate) return false;

        const [sy, sm, sd] = startDate.split("-").map(Number);
        const [ey, em, ed] = endDate.split("-").map(Number);

        const start = new Date(sy, sm - 1, sd);
        const end = new Date(ey, em - 1, ed);

        return reportDate >= start && reportDate <= end;
      }

      return true;
    });
  }, [reports, filterType, startDate, endDate]);

  const analytics = useMemo(() => {
    let dpSold = 0;
    let dpOpp = 0;

    let mcEligible = 0;
    let mcTotal = 0;
    let mcApproved = 0;

    let aalEligible = 0;
    let aalHits = 0;

    const effectiveReports = isStoreMode
      ? filteredReports
      : filteredReports.filter((r) => r.rep === lockedRepCode);

    effectiveReports.forEach((r) => {
      r.customers?.forEach((c) => {
        const productsForCustomer = c.products || [];

        const customerProductsWithMeta = productsForCustomer.map((p) => ({
          ...p,
          meta: productMap[p.name] || null,
        }));

        const isSMB = customerProductsWithMeta.some((p) => p.meta?.isSMB);

        const aalEligibleSales = !isSMB
          ? customerProductsWithMeta.reduce((sum, p) => {
              if (p.qty > 0 && (p.meta?.affectsAAL || p.meta?.aalEligible)) {
                return sum + p.qty;
              }
              return sum;
            }, 0)
          : 0;

        const hasMCAny = customerProductsWithMeta.some(
          (p) => p.qty > 0 && p.meta?.mcType && p.meta.mcType !== "none"
        );

        const hasMCApproved = customerProductsWithMeta.some(
          (p) => p.qty > 0 && p.meta?.mcType === "approved"
        );

        customerProductsWithMeta.forEach((p) => {
          if (p.qty <= 0) return;

          const meta = p.meta;

          if (!meta) {
            const fallbackDPRelevant =
              p.name.includes("HUP") ||
              p.name.includes("TERM") ||
              p.name === "EXPU_H" ||
              p.name === "EXPU_N";

            if (fallbackDPRelevant && !isSMB) {
              const dpEntries = p.dp || [];

              if (dpEntries.length > 0 && typeof dpEntries[0] === "boolean") {
                dpOpp += p.qty;
                dpEntries.forEach((entry) => {
                  if (entry) dpSold++;
                });
              } else if (
                dpEntries.length > 0 &&
                typeof dpEntries[0] === "object"
              ) {
                dpEntries.forEach((entry) => {
                  if (!entry) return;
                  if (entry.value === "HAS") return;
                  dpOpp++;
                  if (entry.value === "YES") dpSold++;
                });
              } else {
                dpOpp += p.qty;
              }
            }

            if (p.name === "DP" && p.qty > 0) {
              dpSold += p.qty;
            }

            return;
          }

          if (meta.dpPositiveStandalone) {
            dpSold += p.qty;
          }

          if (meta.affectsDP && !meta.dpPositiveStandalone && !isSMB) {
            const dpEntries = p.dp || [];

            if (dpEntries.length > 0 && typeof dpEntries[0] === "boolean") {
              dpOpp += p.qty;
              dpEntries.forEach((entry) => {
                if (entry) dpSold++;
              });
            } else if (
              dpEntries.length > 0 &&
              typeof dpEntries[0] === "object"
            ) {
              dpEntries.forEach((entry) => {
                if (!entry) return;
                if (entry.value === "HAS") return;
                dpOpp++;
                if (entry.value === "YES") dpSold++;
              });
            } else {
              dpOpp += p.qty;
            }
          }
        });

        const hasNonExcludedBase = customerProductsWithMeta.some(
          (p) =>
            p.qty > 0 &&
            p.meta &&
            p.meta.affectsMC &&
            !p.meta.mcExcluded &&
            (!p.meta.mcType || p.meta.mcType === "none")
        );

        if (!c.hadMCBefore) {
          if (hasMCAny) {
            mcEligible++;
            mcTotal++;
            if (hasMCApproved) mcApproved++;
          } else if (hasNonExcludedBase) {
            mcEligible++;
          }
        }

        if (aalEligibleSales > 0) {
          aalEligible += aalEligibleSales;
          aalHits += c.aalCount || 0;
        }
      });
    });

    // choose overrides: store for store mode, rep for rep mode
    // in rep mode: use rep overrides only
// in store mode: add store + any rep override for the locked rep
const appliedOverrides = isStoreMode
  ? {
      dpSold: (storeOverrides.dpSold || 0) + (repOverrides.dpSold || 0),
      dpOpp: (storeOverrides.dpOpp || 0) + (repOverrides.dpOpp || 0),
      mcEligible: (storeOverrides.mcEligible || 0) + (repOverrides.mcEligible || 0),
      mcTotal: (storeOverrides.mcTotal || 0) + (repOverrides.mcTotal || 0),
      mcApproved: (storeOverrides.mcApproved || 0) + (repOverrides.mcApproved || 0),
      aalEligible: (storeOverrides.aalEligible || 0) + (repOverrides.aalEligible || 0),
      aalHits: (storeOverrides.aalHits || 0) + (repOverrides.aalHits || 0),
    }
  : repOverrides;
    const finalDpSold = Math.max(0, dpSold + appliedOverrides.dpSold);
    const finalDpOpp = Math.max(0, dpOpp + appliedOverrides.dpOpp);
    const finalMcEligible = Math.max(
      0,
      mcEligible + appliedOverrides.mcEligible
    );
    const finalMcTotal = Math.max(0, mcTotal + appliedOverrides.mcTotal);
    const finalMcApproved = Math.max(
      0,
      mcApproved + appliedOverrides.mcApproved
    );
    const finalAalEligible = Math.max(
      0,
      aalEligible + appliedOverrides.aalEligible
    );
    const finalAalHits = Math.max(0, aalHits + appliedOverrides.aalHits);

    return {
      dpAttach: finalDpOpp
        ? ((finalDpSold / finalDpOpp) * 100).toFixed(1)
        : 0,
      mcTotalRate: finalMcEligible
        ? ((finalMcTotal / finalMcEligible) * 100).toFixed(1)
        : 0,
      mcApprovedRate: finalMcTotal
        ? ((finalMcApproved / finalMcTotal) * 100).toFixed(1)
        : 0,
      aalRate: finalAalEligible
        ? ((finalAalHits / finalAalEligible) * 100).toFixed(1)
        : 0,
      count: effectiveReports.length,
      raw: {
        dpSold: finalDpSold,
        dpOpp: finalDpOpp,
        mcEligible: finalMcEligible,
        mcTotal: finalMcTotal,
        mcApproved: finalMcApproved,
        aalEligible: finalAalEligible,
        aalHits: finalAalHits,
      },
      baseRaw: {
        dpSold,
        dpOpp,
        mcEligible,
        mcTotal,
        mcApproved,
        aalEligible,
        aalHits,
      },
      overrides: appliedOverrides,
    };
  }, [
    filteredReports,
    productMap,
    isStoreMode,
    lockedRepCode,
    storeOverrides,
    repOverrides,
  ]);

  return (
    <div className="attach-page">
      <div className="attach-shell">
        <h2 className="attach-title">Attach Performance</h2>

        <div className="attach-filters">
          <div className="attach-range-buttons">
            <button
              type="button"
              className={`attach-range-btn ${
                filterType === "ALL" ? "active" : ""
              }`}
              onClick={() => setFilterType("ALL")}
            >
              All Time
            </button>

            <button
              type="button"
              className={`attach-range-btn ${
                filterType === "MTD" ? "active" : ""
              }`}
              onClick={() => setFilterType("MTD")}
            >
              MTD
            </button>

            <button
              type="button"
              className={`attach-range-btn ${
                filterType === "QTD" ? "active" : ""
              }`}
              onClick={() => setFilterType("QTD")}
            >
              QTD 🔥
            </button>

            <button
              type="button"
              className={`attach-range-btn ${
                filterType === "YTD" ? "active" : ""
              }`}
              onClick={() => setFilterType("YTD")}
            >
              YTD
            </button>

            <button
              type="button"
              className={`attach-range-btn ${
                filterType === "RANGE" ? "active" : ""
              }`}
              onClick={() => setFilterType("RANGE")}
            >
              Range
            </button>
          </div>

          {filterType === "RANGE" && (
            <div className="attach-range">
              <input
                type="date"
                className="attach-input"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <input
                type="date"
                className="attach-input"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          )}
        </div>

        <div className="attach-main-card">
          <div className="attach-metric-row">
            <span>DP Attach</span>
            <strong>{analytics.dpAttach}%</strong>
          </div>
          <div className="attach-metric-row">
            <span>MC Total Rate</span>
            <strong>{analytics.mcTotalRate}%</strong>
          </div>
          <div className="attach-metric-row">
            <span>MC Approved Rate</span>
            <strong>{analytics.mcApprovedRate}%</strong>
          </div>
          <div className="attach-metric-row">
            <span>AAL Rate</span>
            <strong>{analytics.aalRate}%</strong>
          </div>
        </div>

        <div className="attach-sub-info">
          Reports Loaded: {analytics.count}
        </div>

        <div className="attach-raw-card">
          <div>DP Sold: {analytics.raw.dpSold}</div>
          <div>DP Opp: {analytics.raw.dpOpp}</div>

          <div>MC Eligible: {analytics.raw.mcEligible}</div>
          <div>MC Total: {analytics.raw.mcTotal}</div>
          <div>MC Approved: {analytics.raw.mcApproved}</div>

          <div>AAL Eligible: {analytics.raw.aalEligible}</div>
          <div>AAL Hits: {analytics.raw.aalHits}</div>
        </div>
      </div>
    </div>
  );
}